import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Printer, ClipboardList } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import LastUpdated from '@/components/LastUpdated';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AvanceProgramaticoPage = ({ user, onLogout }) => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [subject, setSubject] = useState('');
  const [columns, setColumns] = useState([]);
  const [topics, setTopics] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved
  const saveTimer = useRef(null);
  const topicsRef = useRef({});
  topicsRef.current = topics;

  const dateColumns = columns.filter((c) => !c.isSeparator);

  const fetchData = useCallback(async (isPoll = false) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/teacher/programmatic-progress/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubject(res.data.subject || '');
      // Columns always reflect the attendance grid (added/moved/removed there).
      setColumns(res.data.columns || []);
      // On first load, take server topics. On polls, DON'T clobber what the
      // teacher may be typing right now; keep local edits.
      if (!isPoll) {
        setTopics(res.data.topics || {});
      }
      setLastUpdated(Date.now());
    } catch (error) {
      if (!isPoll) toast.error('No se pudo cargar el avance programático');
    } finally {
      if (!isPoll) setLoading(false);
    }
  }, [classId]);

  useEffect(() => { fetchData(false); }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try { await fetchData(false); } finally { setRefreshing(false); }
  };

  // Refresh columns from the Attendance section when the tab regains focus
  // (no continuous polling — saves data).
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === 'visible') fetchData(true); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [fetchData]);

  const persist = useCallback(async (nextTopics) => {
    try {
      setSaveStatus('saving');
      const token = localStorage.getItem('token');
      await axios.put(`${API}/teacher/programmatic-progress/${classId}`,
        { topics: nextTopics },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 1500);
    } catch (error) {
      setSaveStatus('idle');
      toast.error('Error al guardar el avance');
    }
  }, [classId]);

  const handleChange = (colId, value) => {
    const next = { ...topicsRef.current, [colId]: value };
    setTopics(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(next), 700);
  };

  const handlePrint = () => {
    const rows = dateColumns.map((c) => `
      <tr>
        <td style="border:1px solid #333;padding:8px 12px;white-space:nowrap">${c.label || c.date || ''}</td>
        <td style="border:1px solid #333;padding:8px 12px">${(topics[c.id] || '').replace(/</g, '&lt;').replace(/>/g, '&gt;') || ''}</td>
      </tr>`).join('');
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Avance Programático - ${subject}</title>
      <style>
        body{font-family:Arial,sans-serif;margin:24px;color:#222}
        h2{margin-bottom:2px} .sub{color:#555;margin-bottom:16px;font-size:13px}
        table{width:100%;border-collapse:collapse;margin-top:8px}
        th{border:1px solid #333;padding:8px 12px;background:#e9d5ff;text-align:left}
        td{border:1px solid #333;padding:8px 12px;vertical-align:top}
        tr:nth-child(even){background:#faf5ff}
        .footer{margin-top:20px;font-size:11px;color:#888;text-align:center}
        @media print{body{margin:10px}}
      </style></head><body>
      <h2>Avance Programático — ${subject}</h2>
      <p class="sub">${dateColumns.length} fecha(s) registrada(s)</p>
      <table>
        <thead><tr><th style="width:140px">Fecha</th><th>Tema desarrollado</th></tr></thead>
        <tbody>${rows || '<tr><td colspan="2" style="border:1px solid #333;padding:12px;text-align:center;color:#888">Aún no hay fechas registradas en Asistencias.</td></tr>'}</tbody>
      </table>
      <p class="footer">Generado el ${new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
      </body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <Button
          variant="outline"
          onClick={() => navigate('/classes')}
          data-testid="avance-back-btn"
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" /> Volver a Clases
        </Button>
        <div className="flex items-center gap-3">
          <LastUpdated timestamp={lastUpdated} onRefresh={handleRefresh} refreshing={refreshing} />
          <span className="text-xs text-slate-500 min-w-[90px] text-right" data-testid="avance-save-status">
            {saveStatus === 'saving' ? 'Guardando…' : saveStatus === 'saved' ? 'Guardado ✓' : ''}
          </span>
          <Button
            onClick={handlePrint}
            data-testid="avance-print-btn"
            className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2"
          >
            <Printer className="w-4 h-4" /> Imprimir
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-2">
        <ClipboardList className="w-6 h-6 text-purple-600" />
        <h1 className="text-3xl font-bold text-slate-900">Avance Programático</h1>
      </div>
      <p className="text-sm text-slate-600 mb-1">{subject}</p>
      <p className="text-xs text-slate-400 mb-6">
        Las fechas se toman automáticamente de tu sección de Asistencias. Escribe el tema visto en cada fecha; se guarda solo.
      </p>

      {loading ? (
        <div className="text-slate-500" data-testid="avance-loading">Cargando…</div>
      ) : dateColumns.length === 0 ? (
        <div className="bg-purple-50 border border-purple-100 rounded-lg p-8 text-center text-slate-600" data-testid="avance-empty">
          Aún no has agregado fechas en la sección de <strong>Asistencias</strong> de esta clase.
          Cuando agregues una fecha ahí, aparecerá aquí automáticamente.
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white" data-testid="avance-grid">
          <table className="border-collapse">
            <tbody>
              {/* Row 1: date headers */}
              <tr>
                <th className="sticky left-0 z-10 bg-slate-100 border border-slate-200 px-3 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                  Fecha
                </th>
                {columns.map((c) => (
                  (c.isSeparator || c.isSuspension) ? (
                    <th
                      key={c.id}
                      className={`border border-slate-200 px-2 py-2 text-[10px] font-semibold text-center whitespace-nowrap ${c.isSuspension ? 'bg-red-100 text-red-700' : 'bg-purple-100 text-purple-700'}`}
                      title={c.isSuspension ? `Clase suspendida — ${c.motivo || 'sin motivo'}` : c.label}
                    >
                      {c.isSuspension ? (
                        <div className="flex flex-col items-center leading-tight">
                          <span>SUSP. {c.label || ''}</span>
                          {c.motivo && (
                            <span className="block max-w-[90px] truncate text-[8px] font-normal normal-case" title={c.motivo}>
                              {c.motivo}
                            </span>
                          )}
                        </div>
                      ) : c.label}
                    </th>
                  ) : (
                    <th
                      key={c.id}
                      className="bg-slate-50 border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 text-center whitespace-nowrap min-w-[140px]"
                      data-testid={`avance-col-header-${c.id}`}
                    >
                      {c.label || c.date}
                    </th>
                  )
                ))}
              </tr>
              {/* Row 2: topic cells */}
              <tr>
                <th className="sticky left-0 z-10 bg-slate-100 border border-slate-200 px-3 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                  Tema
                </th>
                {columns.map((c) => (
                  (c.isSeparator || c.isSuspension) ? (
                    <td key={c.id} className={`border border-slate-200 ${c.isSuspension ? 'bg-red-50' : 'bg-purple-50'}`} />
                  ) : (
                    <td key={c.id} className="border border-slate-200 p-1 align-top min-w-[140px]">
                      <textarea
                        value={topics[c.id] || ''}
                        onChange={(e) => handleChange(c.id, e.target.value)}
                        title={topics[c.id] || ''}
                        placeholder="Tema…"
                        rows={2}
                        className="w-full h-16 resize-none overflow-hidden text-sm px-2 py-1 rounded border border-transparent hover:border-slate-200 focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-300 bg-transparent"
                        data-testid={`avance-topic-${c.id}`}
                      />
                    </td>
                  )
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
};

export default AvanceProgramaticoPage;
